/* iter-cert-rotation-runbook — On-call runbook for Reader Universal-Link
 * cert drift. Opened from the CertPinDiffDialog "Open runbook" button
 * so a paged engineer sees exactly what to do without leaving admin.
 *
 * Kept as a self-contained JSX modal (no markdown library dep) so it
 * renders instantly even if the SPA is booted from a cold cache.
 */
import React from 'react';
import { X, BookOpen, AlertTriangle, CheckCircle2, ExternalLink, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function CertRotationRunbook({ open, onClose }) {
  const bodyRef = React.useRef(null);
  const handlePrint = () => {
    // iter-runbook-pdf — Open a print-optimised window with just the
    // runbook body so a paged engineer can Save-as-PDF from any browser.
    const body = bodyRef.current;
    if (!body) return;
    const w = window.open('', 'runbook_pdf', 'width=900,height=1200');
    if (!w) return;
    w.document.write(`<!doctype html><html><head>
      <title>Cert Rotation Runbook</title>
      <meta charset="utf-8" />
      <style>
        body { font: 12.5px/1.55 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif; color:#111; padding: 32px; max-width: 780px; margin: 0 auto; }
        h1 { font-size: 20px; margin: 0 0 4px; }
        h2 { font-size: 14px; margin: 20px 0 6px; border-bottom: 1px solid #ccc; padding-bottom: 2px; }
        code { background: #f2ede4; padding: 1px 4px; border-radius: 3px; font-size: 11px; }
        ol, ul { margin: 4px 0 4px 22px; padding: 0; }
        li { margin: 3px 0; }
        .brand { color: #7a1c1c; font-weight: 600; text-transform: uppercase; letter-spacing: 1.5px; font-size: 10px; margin-bottom: 4px; }
        .meta  { color: #666; font-size: 10.5px; margin-top: 24px; border-top: 1px dashed #ccc; padding-top: 8px; }
        @page { size: letter; margin: 0.6in; }
      </style>
    </head><body>
      <div class="brand">Crown &amp; Rose · The Loom · On-Call Runbook</div>
      <h1>Reader Cert Rotation — On-Call Runbook</h1>
      ${body.innerHTML}
      <div class="meta">Runbook v1 · Printed ${new Date().toLocaleString()}</div>
    </body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { try { w.print(); } catch { /* no-op */ } }, 300);
  };
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
      data-testid="cert-rotation-runbook-modal"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-md border border-border bg-background shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start gap-2 border-b border-border bg-panel-header/60 px-4 py-3">
          <BookOpen className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div className="flex-1">
            <div className="text-[14px] font-semibold">Reader Cert Rotation — On-Call Runbook</div>
            <div className="text-[11px] text-muted-foreground">One-page playbook for the diff modal alarm. Read top-to-bottom.</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            data-testid="cert-rotation-runbook-close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div ref={bodyRef} className="flex-1 overflow-y-auto px-5 py-4 text-[12.5px] leading-relaxed">
          <section className="mb-4">
            <div className="mb-1 flex items-center gap-1.5 text-[12.5px] font-semibold text-rose-700">
              <AlertTriangle className="h-3.5 w-3.5" /> Why you're here
            </div>
            <p>
              The Loom pins the Indie Reader Android app's SHA-256 signing-cert
              fingerprint(s) so any drift is caught before writers hit "Open in
              Reader" and get a black-hole failure. When the cron detects that
              the Reader's <code>/.well-known/assetlinks.json</code> no longer
              matches the pinned set, a Slack alert fires and a
              <code>[ALERT] Reader Universal-Link cert drift</code> row lands in
              the pro_writer Panel Inbox.
            </p>
          </section>

          <section className="mb-4">
            <div className="mb-1 text-[12.5px] font-semibold">Step 1 — Confirm the drift is real</div>
            <ol className="list-decimal space-y-1 pl-5">
              <li>Open the diff modal you came from — the "Newly added" and "Missing / dropped" sections show the exact fingerprint delta.</li>
              <li>If both are empty, the pin is in-sync and the alert was stale. Dismiss and file a bug against the cron.</li>
              <li>If the "Newly added" set is non-empty and matches an <b>expected Android release</b>, this is a legitimate rotation — continue to Step 2.</li>
              <li>If the "Missing / dropped" set contains fingerprints you don't recognize, or the "Newly added" set doesn't match any release, <b>STOP</b> — treat this as a possible signing-key compromise and escalate to security-oncall.</li>
            </ol>
          </section>

          <section className="mb-4">
            <div className="mb-1 text-[12.5px] font-semibold">Step 2 — Verify with the Reader team</div>
            <ul className="list-disc space-y-1 pl-5">
              <li>Post in <code>#reader-releases</code>: <em>"Universal-link fingerprint drift observed at Loom. Was this expected?"</em>. Include the diff modal's added/dropped list.</li>
              <li>Ask the Reader release engineer to confirm the new fingerprint matches the freshly-signed release APK/AAB (<code>keytool -list -printcert -jarfile app.aab</code>).</li>
              <li>Wait for a two-person ack before accepting the rotation.</li>
            </ul>
          </section>

          <section className="mb-4">
            <div className="mb-1 text-[12.5px] font-semibold">Step 3 — Rotate the pin (only after Step 2 ack)</div>
            <ol className="list-decimal space-y-1 pl-5">
              <li>Back in the diff modal, type <code className="rounded bg-muted px-1">ROTATE</code> in the confirmation input.</li>
              <li>Click <b>Overwrite pin</b>. The backend re-fetches the manifest, replaces <code>app_config.reader_pinned_cert_fingerprints</code>, and stamps <code>reader_pinned_cert_fingerprints_at</code>.</li>
              <li>The next cron tick (≤ 6h) confirms the pin. If you need instant re-verification, run <code>python -m app.reader_cert_rotation_cron</code> in a shell.</li>
            </ol>
          </section>

          <section className="mb-4">
            <div className="mb-1 text-[12.5px] font-semibold">Step 4 — Post-rotation checks</div>
            <ul className="list-disc space-y-1 pl-5">
              <li>Click "Open in Reader" from a test writer's Desktop Notebook — the universal link should hand off cleanly.</li>
              <li>Watch <code>reader_cert_last_status</code> flip from <code>drift</code> → <code>ok</code> on the next tick.</li>
              <li>Close the Panel Inbox alert row (<b>Mark read</b> + reply "Rotation confirmed at <em>&lt;time&gt;</em> by <em>&lt;you&gt;</em>").</li>
            </ul>
          </section>

          <section className="mb-4">
            <div className="mb-1 text-[12.5px] font-semibold">Step 5 — If in doubt, DO NOT rotate</div>
            <p>
              An accidental pin overwrite lets a malicious APK impersonate the
              Reader for every writer. When you can't reach a Reader release
              engineer within the SLA window, leave the pin as-is. Writers will
              see a friendly fallback error on "Open in Reader" — inconvenient
              but safe. Return during business hours with two-person ack.
            </p>
          </section>

          <section className="mb-4">
            <div className="mb-1 flex items-center gap-1.5 text-[12.5px] font-semibold text-emerald-700">
              <CheckCircle2 className="h-3.5 w-3.5" /> Quick reference
            </div>
            <ul className="list-disc space-y-1 pl-5">
              <li><b>Cron interval</b>: <code>READER_CERT_ROTATION_INTERVAL_SECONDS</code> (default 21600s / 6h).</li>
              <li><b>Slack channel</b>: whatever <code>SLACK_WEBHOOK_URL</code> points at. Test it any time with the "Send Slack test ping" button.</li>
              <li><b>Manifest URL source</b>: <code>READER_DEEP_LINK_HOST</code> env or fallback <code>https://transfer-limit-read.emergent.host</code>.</li>
              <li><b>Bootstrap</b>: The first tick after a pin wipe silently adopts the current fingerprint set (no alarm). Use the diff modal's "Bootstrap pin" flow if you want to re-establish the pin manually.</li>
            </ul>
          </section>

          <section className="mb-1">
            <div className="mb-1 text-[12.5px] font-semibold">Contacts</div>
            <ul className="list-disc space-y-1 pl-5">
              <li>Reader release engineer: <code>#reader-releases</code> Slack.</li>
              <li>Security on-call: <code>security-oncall@crownnrose.com</code>.</li>
              <li>Loom platform: <code>ops@crownnrose.com</code>.</li>
            </ul>
          </section>
        </div>

        <footer className="flex items-center justify-between gap-2 border-t border-border bg-panel-header/40 px-4 py-2.5 text-[11px] text-muted-foreground">
          <span>Runbook v1 · Last updated 2026-09-17</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={handlePrint} className="h-7 gap-1 text-[11px]"
                    data-testid="cert-rotation-runbook-pdf-btn">
              <Printer className="h-3 w-3" /> Download PDF
            </Button>
            <a
              href="https://developer.android.com/training/app-links/verify-android-applinks"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
              data-testid="cert-rotation-runbook-android-docs"
            >
              Android App Links docs <ExternalLink className="h-3 w-3" />
            </a>
            <Button size="sm" variant="ghost" onClick={onClose} data-testid="cert-rotation-runbook-dismiss">
              Close
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}
